import type { ChequeDaPessoa, ChequeDaSemana, Programa } from '../../premiacao';
import { PROGRAMAS, somaQtd } from './conta-da-semana';

/**
 * TUDO POR BALCONISTA (dono, 05/10/2026): numa tela só, quanto cada balconista vendeu e quanto
 * voltou em devolução — vitamina, pré-vencido, paradão e o total. A conta é a MESMA do cheque da
 * Conferência (nada é recalculado aqui):
 *
 * - VENDIDO = o que ela lançou e o HOS mostra vendido — bateu, abaixo do card, dia errado e lote do
 *   dia. "Lançou e não vendeu" não é venda; "vendeu e não lançou" não é dela no programa.
 * - DEVOLVIDO = as devoluções do HOS que voltaram contra a venda dela.
 * - PREMIAÇÃO = o devido do programa; o total é o "a receber" do cheque (depois de canceladas,
 *   devoluções e meta), por isso pode ser menor que a soma dos programas.
 */

export type NumerosDoPrograma = {
  readonly vendido: number;
  readonly devolvido: number;
  readonly liquido: number;
  readonly premiacao: number;
};

export type LinhaTudoJunto = {
  readonly pessoaId: string;
  readonly nome: string;
  readonly codigo: string | null;
  readonly programas: Readonly<Record<Programa, NumerosDoPrograma>>;
  readonly total: NumerosDoPrograma;
};

export function numerosDoPrograma(p: ChequeDaPessoa, programa: Programa): NumerosDoPrograma {
  const doP = <T extends { programa: string; qtd: number }>(xs: readonly T[] | undefined) =>
    somaQtd((xs ?? []).filter((x) => x.programa === programa));
  const L = p.linhas;
  const vendido = doP(L.bate) + doP(L.abaixoDoCard) + doP(L.diaErrado) + doP(L.loteDoMesmoDia);
  const devolvido = doP(L.devolvidos);
  return {
    vendido,
    devolvido,
    liquido: vendido - devolvido,
    premiacao: Math.max(0, p.tipos[programa]?.devido ?? 0),
  };
}

export function linhaTudoJunto(p: ChequeDaPessoa): LinhaTudoJunto {
  const programas = Object.fromEntries(
    PROGRAMAS.map((x) => [x.id, numerosDoPrograma(p, x.id)]),
  ) as Record<Programa, NumerosDoPrograma>;
  const somar = (k: 'vendido' | 'devolvido' | 'liquido') =>
    PROGRAMAS.reduce((s, x) => s + programas[x.id][k], 0);
  return {
    pessoaId: p.pessoaId,
    nome: p.nome,
    codigo: p.codigo,
    programas,
    total: {
      vendido: somar('vendido'),
      devolvido: somar('devolvido'),
      liquido: somar('liquido'),
      premiacao: p.aReceber,
    },
  };
}

/** Uma linha por balconista que vendeu ou teve devolução no período, quem mais vendeu primeiro. */
export function tudoPorBalconista(ch: ChequeDaSemana): LinhaTudoJunto[] {
  return ch.pessoas
    .map(linhaTudoJunto)
    .filter((l) => l.total.vendido + l.total.devolvido > 0 || l.total.premiacao > 0)
    .sort(
      (a, b) =>
        b.total.liquido - a.total.liquido ||
        b.total.premiacao - a.total.premiacao ||
        a.nome.localeCompare(b.nome, 'pt-BR'),
    );
}

/** A linha de TOTAL da loja (soma das pessoas na tela). */
export function totalDaEquipe(
  linhas: readonly LinhaTudoJunto[],
): Omit<LinhaTudoJunto, 'pessoaId' | 'nome' | 'codigo'> {
  const zero = (): NumerosDoPrograma => ({ vendido: 0, devolvido: 0, liquido: 0, premiacao: 0 });
  const mais = (a: NumerosDoPrograma, b: NumerosDoPrograma): NumerosDoPrograma => ({
    vendido: a.vendido + b.vendido,
    devolvido: a.devolvido + b.devolvido,
    liquido: a.liquido + b.liquido,
    premiacao: Math.round((a.premiacao + b.premiacao) * 100) / 100,
  });
  const programas = Object.fromEntries(
    PROGRAMAS.map((x) => [x.id, linhas.reduce((s, l) => mais(s, l.programas[x.id]), zero())]),
  ) as Record<Programa, NumerosDoPrograma>;
  return { programas, total: linhas.reduce((s, l) => mais(s, l.total), zero()) };
}
